import { migrateProposalOptions } from '../utils';

// ─── v4 API — Public WebSocket (wss://api.derivws.com/trading/v1/options/ws/public) ──────────────

export const getActiveSymbolsBrief = () => ({
    active_symbols: 'brief',
});

export const getActiveSymbolsFull = () => ({
    active_symbols: 'full',
});

export const getContractsForSymbol = (symbol) => ({
    // Accepts a raw symbol string OR an active_symbols object — normalise both.
    contracts_for: (symbol && typeof symbol === 'object')
        ? (symbol.underlying_symbol || symbol.symbol || symbol)
        : symbol,
});

export const unsubscribeByID = (id) => ({
    forget: id,
});

export const unsubscribeFromTick = (symbol) => ({
    forget: symbol.underlying_symbol || symbol.symbol || symbol,
});

/**
 * Unsubscribe from multiple tick streams.
 *
 * @deprecated The `forget` endpoint only accepts a single subscription ID per
 * call. This helper has been changed to emit `forget_all: 'ticks'`, which is
 * the correct API-supported way to stop all tick subscriptions at once. If you
 * need to stop individual streams, call `unsubscribeByID(subscriptionId)` for
 * each ID returned from the subscription response.
 */
export const unsubscribeFromTicks = (_symbols) => ({
    forget_all: 'ticks',
});

export const unsubscribeFromAllTicks = () => ({
    forget_all: 'ticks',
});

export const unsubscribeFromAllCandles = () => ({
    forget_all: 'candles',
});

export const unsubscribeFromAllProposals = () => ({
    forget_all: 'proposal',
});

export const unsubscribeFromAllPortfolios = () => ({
    forget_all: 'portfolio',
});

export const ping = () => ({
    ping: 1,
});

export const getPriceProposalForContract = (options) => ({
    proposal: 1,
    ...migrateProposalOptions(options),
});

export const subscribeToPriceForContractProposal = (options) => ({
    proposal: 1,
    subscribe: 1,
    ...migrateProposalOptions(options),
});

/**
 * Subscribe to real-time tick stream for a single symbol.
 * Send the symbol string (e.g. `'R_100'`) or an active_symbols object.
 */
export const subscribeToTick = (symbol) => ({
    ticks: (symbol && typeof symbol === 'object')
        ? (symbol.underlying_symbol || symbol.symbol || symbol)
        : symbol,
});

/**
 * Subscribe to tick streams. Each symbol becomes an individual WS send.
 * Note: passing multiple symbols in one message is not supported by the API;
 * this function sends a single ticks message and is only meaningful for a
 * single symbol. For multiple symbols make separate calls.
 */
export const subscribeToTicks = (symbols) => ({
    ticks: Array.isArray(symbols)
        ? (symbols[0] && typeof symbols[0] === 'object'
            ? (symbols[0].underlying_symbol || symbols[0].symbol || symbols[0])
            : symbols[0])
        : symbols,
});

export const getTickHistory = (symbol, options = {}) => ({
    ticks_history: (symbol && typeof symbol === 'object')
        ? (symbol.underlying_symbol || symbol.symbol || symbol)
        : symbol,
    end: Math.floor(Date.now() / 1000),
    ...options,
});

export const getCandles = (symbol, options = {}) => ({
    ticks_history: (symbol && typeof symbol === 'object')
        ? (symbol.underlying_symbol || symbol.symbol || symbol)
        : symbol,
    style: 'candles',
    end: Math.floor(Date.now() / 1000),
    ...options,
});

export const getCandlesForLastNDays = (symbol, ndays) => ({
    ticks_history: (symbol && typeof symbol === 'object')
        ? (symbol.underlying_symbol || symbol.symbol || symbol)
        : symbol,
    style: 'candles',
    start: Math.floor(Date.now() / 1000) - (ndays - 1) * 60 * 60 * 24,
    end: Math.floor(Date.now() / 1000),
    granularity: 60 * 60 * 24,
    count: 30,
});

export const getServerTime = () => ({
    time: 1,
});

export const getTradingTimes = (date) => ({
    trading_times: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
});

// ─── v3-only — Not available on v4 OTP connections ────────────────────────────
// These WebSocket messages are not part of the Deriv v4 API. They will fail
// silently if sent on a rawApiUrl (OTP-authenticated) connection. Only use
// them in legacyMode connections.

/**
 * @deprecated v3 only. Not available in the Deriv v4 API.
 * In v4, authenticate by obtaining an OTP URL via
 * `POST /trading/v1/options/accounts/{accountId}/otp` (see `DerivREST.getOTPWebSocketUrl`)
 * and connecting to that URL directly — do not send an `authorize` message.
 */
/* istanbul ignore next -- v3-only */
export const authorize = (token) => ({
    authorize: token,
});

/**
 * @deprecated v3 only. `get_session_token` does not exist in the Deriv v4 API.
 */
/* istanbul ignore next -- v3-only */
export const getSessionToken = (oneTimeToken) => ({
    get_session_token: oneTimeToken,
});

/**
 * @deprecated v3 only. `asset_index` is not available in the Deriv v4 API.
 */
/* istanbul ignore next -- v3-only */
export const getAssetIndex = () => ({
    asset_index: 1,
});

/**
 * @deprecated v3 only. `landing_company` is not available in the Deriv v4 API.
 * Use REST account endpoints or the v4 WebSocket trading surface instead.
 */
/* istanbul ignore next -- v3-only */
export const getLandingCompany = (landingCompany) => ({
    landing_company: landingCompany,
});

/**
 * @deprecated v3 only. `landing_company_details` is not available in the Deriv v4 API.
 */
/* istanbul ignore next -- v3-only */
export const getLandingCompanyDetails = (landingCompany) => ({
    landing_company_details: landingCompany,
});

/**
 * @deprecated v3 only. `new_account_virtual` is not available in the Deriv v4 API.
 * Use `POST /trading/v1/options/accounts` via `DerivREST.createAccount` instead.
 */
/* istanbul ignore next -- v3-only */
export const createVirtualAccount = (options) => ({
    new_account_virtual: 1,
    ...options,
});

/**
 * @deprecated v3 only. `paymentagent_list` is not available in the Deriv v4 API.
 * Use `GET /payment-agents/v1/agents` via `DerivREST.getPaymentAgents` instead.
 */
/* istanbul ignore next -- v3-only */
export const getPaymentAgentsForCountry = (countryCode) => ({
    paymentagent_list: countryCode,
});

/**
 * @deprecated v3 only. `payout_currencies` is not available in the Deriv v4 API.
 */
/* istanbul ignore next -- v3-only */
export const getPayoutCurrencies = () => ({
    payout_currencies: 1,
});

/**
 * @deprecated v3 only. `residence_list` is not available in the Deriv v4 API.
 */
/* istanbul ignore next -- v3-only */
export const getResidences = () => ({
    residence_list: 1,
});

/**
 * @deprecated v3 only. `states_list` is not available in the Deriv v4 API.
 */
/* istanbul ignore next -- v3-only */
export const getStatesForCountry = (countryCode) => ({
    states_list: countryCode,
});

/**
 * @deprecated v3 only. `verify_email` is not available in the Deriv v4 API.
 */
/* istanbul ignore next -- v3-only */
export const verifyEmail = (email, type) => ({
    verify_email: email,
    type,
});

/**
 * @deprecated v3 only. `website_status` is not available in the Deriv v4 API.
 */
export const getWebsiteStatus = (options = {}) => ({
    website_status: 1,
    ...options,
});
